import { useRef, useState } from 'react';

import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { useController, useFormContext } from 'react-hook-form';

import type { ServiceTypeEnum } from 'src/common/external/bambi-api/bambiApi';
import {
  serviceTypeOptions,
  serviceTypeOptionsMap,
} from 'src/features/add-trip/ServiceDetails/serviceTypeOptions';

type ServiceTypeSelectorProps = {
  fieldPath: string;
};

const allServiceTypes = serviceTypeOptions.map(
  (o) => o.value as ServiceTypeEnum
);

export function ServiceTypeSelector({ fieldPath }: ServiceTypeSelectorProps) {
  const { control, clearErrors } = useFormContext();
  const isRestrictedRef = useRef(false);
  const checkedTypesRef = useRef<ServiceTypeEnum[]>(allServiceTypes);

  const { field, fieldState } = useController({
    name: fieldPath,
    control,
    rules: {
      validate: {
        hasSelectionWhenRestricted: () => {
          if (isRestrictedRef.current && checkedTypesRef.current.length === 0) {
            return 'Select at least one service type, or reset to all types.';
          }
          return true;
        },
      },
    },
  });

  const [isRestricted, setIsRestrictedInternal] = useState(() => {
    const initial = (field.value?.length ?? 0) > 0;
    isRestrictedRef.current = initial;
    return initial;
  });

  const [checkedTypes, setCheckedTypesInternal] = useState<ServiceTypeEnum[]>(
    () => {
      if ((field.value?.length ?? 0) > 0) {
        checkedTypesRef.current = field.value;
        return field.value;
      }
      return allServiceTypes;
    }
  );

  const setCheckedTypes = (types: ServiceTypeEnum[]) => {
    checkedTypesRef.current = types;
    setCheckedTypesInternal(types);
  };

  const setIsRestricted = (val: boolean) => {
    isRestrictedRef.current = val;
    setIsRestrictedInternal(val);
  };

  const handleToggleType = (value: ServiceTypeEnum, checked: boolean) => {
    const newTypes = checked
      ? [...new Set([...checkedTypes, value])]
      : checkedTypes.filter((t) => t !== value);

    setCheckedTypes(newTypes);

    const allSelected = serviceTypeOptions.every((opt) =>
      newTypes.includes(opt.value as ServiceTypeEnum)
    );
    // All selected means no restriction — pass [] to backend
    field.onChange(allSelected ? [] : newTypes);
    if (allSelected) clearErrors(fieldPath);
  };

  const handleRestrict = () => {
    setIsRestricted(true);
    setCheckedTypes(allServiceTypes);
    field.onChange([]);
  };

  const handleReset = () => {
    setIsRestricted(false);
    field.onChange([]);
    clearErrors(fieldPath);
  };

  if (!isRestricted) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-teal-200 px-4 py-3">
        <span className="text-sm font-semibold text-gray-900">
          Service Types
        </span>
        <span className="rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
          All types included
        </span>
        <button
          type="button"
          className="ml-auto text-sm font-medium text-teal-600 underline"
          onClick={handleRestrict}
        >
          Restrict to specific types
        </button>
      </div>
    );
  }

  const allSelected = serviceTypeOptions.every((opt) =>
    checkedTypes.includes(opt.value as ServiceTypeEnum)
  );
  const selectedLabels = checkedTypes
    .map((t) => serviceTypeOptionsMap[t])
    .join(', ');

  return (
    <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">
          Service Types
        </span>
        <button
          type="button"
          className="text-sm font-medium text-red-500 underline"
          onClick={handleReset}
        >
          Reset to all types
        </button>
      </div>
      <fieldset>
        <legend className="sr-only">Select service types to restrict</legend>
        <div className="grid grid-cols-2 gap-2">
          {serviceTypeOptions.map((option) => {
            const value = option.value as ServiceTypeEnum;
            const isChecked = checkedTypes.includes(value);
            return (
              <label
                key={value}
                htmlFor={`service-type-${value}`}
                className={`flex cursor-pointer items-center gap-2 rounded-md border bg-white p-2 ${
                  isChecked ? 'border-teal-500' : 'border-gray-300'
                }`}
              >
                <CheckBox
                  id={`service-type-${value}`}
                  checked={isChecked}
                  onCheckedChange={(checked) =>
                    handleToggleType(value, checked === true)
                  }
                />
                <span className="text-sm font-medium text-gray-700">
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {fieldState.error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {fieldState.error.message}
        </p>
      )}
      <div role="status" aria-live="polite" aria-atomic="true">
        {!allSelected && checkedTypes.length > 0 && (
          <div
            data-testid="service-type-selector-warning"
            className="mt-3 flex gap-2 rounded-md border border-yellow-400 bg-yellow-100 p-3"
          >
            <ExclamationTriangleIcon
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600"
              aria-hidden="true"
            />
            <p className="text-sm text-yellow-800">
              This pricing model will <strong>only</strong> generate prices for{' '}
              <strong>{selectedLabels}</strong> trips. Trips with other service
              types won't be priced by this model.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
